import { useCallback, useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Grid, IconButton, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PeopleIcon from '@mui/icons-material/People';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HistoryIcon from '@mui/icons-material/History';
import StoreIcon from '@mui/icons-material/Store';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import api from '../../../services/api';
import { formatCpfCnpj, formatDateBR, formatMoney, formatPhone } from '../../../utils';
import { useTranslation } from '../../../hooks/useTranslation';
import { Field, SectionCard } from '../../resaleSuperAdmin/resaleDetails';
import CustomTable from '../../../components/CustomTable';

const isCnpj = (taxvar) => !!taxvar && taxvar.replace(/\D/g, '').length === 14;

const formatBRPhone = (value) => {
  if (!value) return '-';
  const digits = value.replace(/\D/g, '');
  const local = digits.startsWith('55') && digits.length === 13 ? digits.slice(2) : digits;
  return formatPhone(local) || local;
};

const STAGE_CONFIG = {
  PENDING:  { label: 'Pendente',  color: '#fff8e1', textColor: '#f57f17' },
  APPROVED: { label: 'Aprovado',  color: '#e8f5e9', textColor: '#2e7d32' },
  REJECTED: { label: 'Reprovado', color: '#fce4ec', textColor: '#c62828' },
};

const StageChip = ({ stage }) => {
  const cfg = STAGE_CONFIG[stage] ?? { label: stage ?? '-', color: '#f5f5f5', textColor: '#757575' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ backgroundColor: cfg.color, color: cfg.textColor, fontWeight: 700, fontSize: '11px', borderRadius: '6px', height: '22px' }}
    />
  );
};

const DadosClienteTab = ({ simulation, onNavigateToSimulation }) => {
  const { t } = useTranslation();

  const [simulations, setSimulations] = useState([]);
  const [loadingSimulations, setLoadingSimulations] = useState(false);
  const [loadingFile, setLoadingFile] = useState(null);

  const customer = simulation?.customer ?? {};
  const address  = customer?.address ?? {};
  const partners = customer?.relationships ?? [];
  const isPj     = isCnpj(customer.taxvar);

  const customerDocs = (customer?.custom_fields?.documents ?? []).map((d) => ({
    ...d,
    _source: t('simulation.adm.detail.client.sourceCustomer'),
  }));
  const simulationDocs = (simulation?.custom_fields?.documents ?? []).map((d) => ({
    ...d,
    _source: t('simulation.adm.detail.client.sourceSimulation'),
  }));
  const allDocuments = [...customerDocs, ...simulationDocs];

  const fetchSimulations = useCallback(async () => {
    if (!customer?.id) return;
    try {
      setLoadingSimulations(true);
      const { data } = await api.get('/v1/simulation-super-adm/list', {
        params: { customer_id: customer.id },
      });
      setSimulations(data?.list ?? []);
    } catch (error) {
      console.error('Erro ao buscar histórico de simulações:', error);
    } finally {
      setLoadingSimulations(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    fetchSimulations();
  }, [fetchSimulations]);

  const getSignedUrl = async (filePath) => {
    const { data } = await api.get('/v1/simulation-super-adm/file-url', {
      params: { path: filePath },
    });
    return data?.url ?? null;
  };

  const handleView = async (doc) => {
    try {
      setLoadingFile(`view_${doc.url}`);
      const url = await getSignedUrl(doc.url);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Erro ao visualizar arquivo:', error);
    } finally {
      setLoadingFile(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      setLoadingFile(`dl_${doc.url}`);
      const filename = doc.original_name ?? doc.stored_name ?? 'documento';
      const response = await api.get('/v1/simulation-super-adm/download', {
        params: { path: doc.url, name: filename },
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
    } finally {
      setLoadingFile(null);
    }
  };

  const simulationColumns = [
    {
      field: 'total_value',
      headerName: t('simulation.value'),
      render: (value) => formatMoney(value),
    },
    {
      field: 'stage',
      headerName: t('forms.status'),
      render: (value) => <StageChip stage={value} />,
    },
    {
      field: 'created_at',
      headerName: t('simulation.date'),
      render: (value) => formatDateBR(value, false),
    },
  ];

  const simulationActions = [
    {
      icon: <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />,
      tooltip: t('common.view'),
      onClick: (row) => onNavigateToSimulation?.(row.id),
    },
  ];

  return (
    <Box>
      {/* Dados Cadastrais */}
      <SectionCard
        icon={<PersonOutlineIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('simulation.adm.detail.client.generalData')}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.fullName')} value={customer.full_name} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.cpfCnpj')} value={formatCpfCnpj(customer.taxvar)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.email')} value={customer.email} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.phone')} value={customer.cellphone ? formatBRPhone(customer.cellphone) : null} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Dados Empresariais — somente PJ */}
      {isPj && (
        <SectionCard
          icon={<BusinessIcon sx={{ fontSize: 18, color: '#757575' }} />}
          title={t('simulation.adm.detail.client.businessData')}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.companyName')} value={customer.custom_fields?.business_name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.municipalRegistration')} value={customer.custom_fields?.municipal_registration} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.stateRegistration')} value={customer.custom_fields?.state_registration} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.regimeType')} value={customer.custom_fields?.tax_regime} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.financialEmail')} value={customer.custom_fields?.financial_email} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.phone')} value={customer.custom_fields?.financial_phone ? formatBRPhone(customer.custom_fields.financial_phone) : null} />
            </Grid>
          </Grid>


        </SectionCard>
      )}

      {/* Endereço — CPF e CNPJ */}
      {(address.place || address.city || address.zipcode) && (
        <SectionCard
          icon={<LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#757575' }} />}
          title={t('simulation.adm.detail.client.addressData')}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Field label={t('forms.place')} value={address.place} />
            </Grid>
            <Grid item xs={4} md={2}>
              <Field label={t('forms.number')} value={address.number} />
            </Grid>
            <Grid item xs={8} md={4}>
              <Field label={t('forms.complement')} value={address.complement} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Field label={t('forms.neighborhood')} value={address.neighborhood} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Field label={t('forms.city')} value={address.city} />
            </Grid>
            <Grid item xs={4} md={3}>
              <Field
                label={t('forms.state')}
                value={
                  typeof address.region === 'string'
                    ? address.region
                    : address.region?.name
                }
              />
            </Grid>
            <Grid item xs={4} md={3}>
              <Field label={t('forms.zipCode')} value={address.zipcode} />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Sócios — somente PJ */}
      {isPj && (
        <SectionCard
          icon={<PeopleIcon sx={{ fontSize: 18, color: '#757575' }} />}
          title={t('simulation.adm.detail.client.partners')}
        >
          {partners.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: '#bdbdbd', fontStyle: 'italic' }}>
              {t('simulation.adm.detail.client.noPartners')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {partners.map((partner, index) => (
                <Box
                  key={index}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    p: 2,
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#9e9e9e', mb: '2px' }}>
                        {t('forms.name')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                        {partner.people?.full_name ?? '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#9e9e9e', mb: '2px' }}>
                        {t('forms.email')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                        {partner.people?.email ?? '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#9e9e9e', mb: '2px' }}>
                        {t('forms.phone')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                        {partner.people?.phone ? formatBRPhone(partner.people.phone) : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#9e9e9e', mb: '2px' }}>
                        {t('forms.cpf')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                        {partner.people?.taxvar ? formatCpfCnpj(partner.people.taxvar) : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#9e9e9e', mb: '2px' }}>
                        {t('forms.document')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: partner.people?.document ? '#374151' : '#bdbdbd', fontStyle: partner.people?.document ? 'normal' : 'italic' }}>
                        {partner.people?.document ?? t('messages.noInformation')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      )}

      {/* Documentos Anexados */}
      <SectionCard
        icon={<AttachFileIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('simulation.adm.detail.client.documents')}
      >
        {allDocuments.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: '#bdbdbd', fontStyle: 'italic' }}>
            {t('simulation.adm.detail.client.noDocuments')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {allDocuments.map((doc, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#fafafa',
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#212121' }}>
                    {doc.original_name ?? doc.stored_name ?? '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>
                    {doc._source}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    disabled={!!loadingFile}
                    onClick={() => handleView(doc)}
                    title={t('common.view')}
                  >
                    {loadingFile === `view_${doc.url}` ? (
                      <CircularProgress size={16} />
                    ) : (
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={!!loadingFile}
                    onClick={() => handleDownload(doc)}
                    title={t('common.download')}
                  >
                    {loadingFile === `dl_${doc.url}` ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>

      {/* Histórico de Simulações */}
      <SectionCard
        icon={<HistoryIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('simulation.adm.detail.client.simulationHistory')}
      >
        {loadingSimulations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <CustomTable
            columns={simulationColumns}
            rows={simulations}
            actions={simulationActions}
            emptyMessage={t('simulation.adm.detail.client.noSimulations')}
            elevation={0}
            rowsPerPageOptions={[5, 10]}
          />
        )}
      </SectionCard>

      {/* Revenda Vinculada */}
      <SectionCard
        icon={<StoreIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('simulation.adm.detail.client.linkedResale')}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4caf50' }}>
          {simulation?.company?.name ?? '-'}
        </Typography>
      </SectionCard>
    </Box>
  );
};

export default DadosClienteTab;
